//================================================================
//  Component: Groups Pane
//================================================================

//  Purpose: Pane to add/remove groups to an application

//  Properties:
//    - title = {String, title of the sidepane, typically the column name}
//    - collectionid = {String, Firestore collection id}
//    - documentid = {String, Firestore document id}
//    - fieldPath = {String, Firestore field path}
//    - defaultGroups = {Array, list of groups who have access}
//    - paneOpen = {useState, used to determine if the modal is open/closed}
//    - setPaneOpen = {useState, used to determine if the modal is open/closed}


//  Example:
//    <GroupsPane
//      title={`Azure AD Groups | ${applicationDoc?.applicationname}`}
//      collectionid={'applications'}
//      documentid={applicationDoc?.applicationid}
//      fieldPath={'groups'}
//      defaultGroups={applicationDoc?.groups}
//      paneOpen={openGroupsPane}
//      setPaneOpen={setOpenGroupsPane}
//    ></GroupsPane>   

//================================================================


//Libraries
import React, { useState, useEffect, useReducer, useContext } from 'react';

//Contexts
import { GetUser, GetRoles, SetToast } from '../../Library/GlobalContexts';

//Components

//Functions
import GetDocument from '../../Library/GetDocument';
import QueryDocument from '../../Library/QueryDocument';
import AddRemoveDocumentArray from '../../Library/AddRemoveDocumentArray';
import CheckInventoryRole from '../../Library/CheckInventoryRole';

//Images
import Error from '../Images/Icon_Error_Red.svg';
import Add from '../Images/Icon_Add_Teal.svg';
import AddDisabled from '../Images/Icon_AddDisabled_Teal.svg';
import LoadingIcon from '../Images/Image_Loading_Ripple.svg';
import ClearSearch from '../Images/Icon_Clear_Grey.svg';
import SearchIcon from '../Images/Icon_Search_Grey.svg'


export default function GroupsPane({
  title,
  collectionid,
  documentid,
  fieldPath,
  defaultGroups,
  paneOpen,
  setPaneOpen,
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);
  const setToast = useContext(SetToast);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to set pane status > 'pending', 'onload', 'error'
  const [paneStatus, setPaneStatus] = useState('pending');

  // Role checks
  const [roleStatus, setRoleStatus] = useState('read'); // read, download, create, write & admin

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store details about the groups
  const [groupsData, setGroupsData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'paginatedGroups': [],
      'addedGroups': [],
      'removedGroups': [],

      // Add Groups Search
      'newGroupInput': '',
      'queryMessage': '',
      'searchResults': []
    }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Function used to handle search of groups
  function handleGroupSearch(value) {

    if (value.length <= 6) {

      return setGroupsData({
        'queryMessage': 'Search requires more than six characters.',
        'searchResults': [],
      });

    }

    // Reset search
    setGroupsData({
      'queryMessage': '',
    });

    const query = value.toLowerCase();

    // Search by characters in group display name
    QueryDocument('groups', [['searcharray', 'array-contains-any', [query]]])
      .then((groups) => {

        setGroupsData({
          'searchResults': groups,
          'queryMessage': `Found ${groups.length} result(s)`
        });

      })
      .catch((error) => {

        console.log(error);
        return setGroupsData({ 'queryMessage': 'Oops something went wrong!' });

      });

  }

  // Function used to add a group
  function addGroup(group) {

    // Remove the group name from the 'removedGroups' array
    const removedGroups = groupsData.removedGroups.filter(object => object !== group);

    // Update the new groups
    setGroupsData({
      'paginatedGroups': [...groupsData.paginatedGroups, group],
      'addedGroups': [...groupsData.addedGroups, group],
      'removedGroups': removedGroups,

      // Add Groups Search
      'newGroupInput': '',
      'queryMessage': '',
      'searchResults': [],
    });

  }

  // Function used to remove a group
  function removeGroup(group) {

    // Remove the displayname from the 'addedGroups' array
    const addedGroups = groupsData.addedGroups.filter(object => object !== group);
    const paginatedGroups = groupsData.paginatedGroups.filter(object => object !== group);

    setGroupsData({
      'paginatedGroups': paginatedGroups,
      'addedGroups': addedGroups,
      'removedGroups': [...groupsData.removedGroups, group],

      // Add Groups Search
      'newGroupInput': '',
      'queryMessage': '',
      'searchResults': [],

    });

  }

  // Function used to save changes
  function handleSave() {

    // Hide Search Results
    setGroupsData({
      'newGroupInput': '',
      'queryMessage': '',
      'searchResults': []
    });

    const docWritePromises = [];

    // Who made the change?
    const lastmodifiedby = {
      'email': getUser.emailaddress,
      'givenname': getUser.givenname,
      'surname': getUser.surname,
    };

    // Add groups
    groupsData.addedGroups.forEach((group) => {

      const groupObject = {
        'groupid': group?.groupid,
        'displayname': group?.displayname,
        'description': group?.description 
      }

      docWritePromises.push(
        AddRemoveDocumentArray(collectionid, documentid, fieldPath, groupObject, 'add', lastmodifiedby),
      );

    });

    // Remove groups
    groupsData.removedGroups.forEach((group) => {

      const groupObject = {
        'groupid': group?.groupid,
        'displayname': group?.displayname,
        'description': group?.description 
      }

      docWritePromises.push(
        AddRemoveDocumentArray(collectionid, documentid, fieldPath, groupObject, 'remove', lastmodifiedby),
      );

    });


    // Settle promises
    Promise.all(docWritePromises)
      .then(() => {

        // Success toast
        setToast({
          'type': 'success',
          'message': 'Successfully updated groups.'
        });

        // Close Search Results
        setPaneOpen(false);

      }).catch((error) => {

        console.log('error', error);
        setPaneStatus('error');

      });

  }

  //------------------------------------------------------
  //  useEffect
  //------------------------------------------------------

  // Onload > Get the groups docs
  useEffect(() => {

    if (defaultGroups === undefined) return;
    if (getRoles === undefined) return;

    // ============================================
    //  Perform role check
    // ============================================

    const roleStatus = CheckInventoryRole(collectionid, getRoles, getUser);
    if (roleStatus === 'access-denied') return setPaneOpen(undefined); // If role check files, close pane
    setRoleStatus(roleStatus);

    // ============================================
    //  Fetch Data
    // ============================================

    const docPromises = [];

    // Loop through groups > Get Document
    // https://masteringjs.io/tutorials/fundamentals/foreach-break
    defaultGroups.every((group, index) => {

      if (index >= 500) return false;

      docPromises.push(
        GetDocument('groups', group?.groupid),
      );

      return true;

    });

    // Resolve promises
    Promise.all(docPromises)
      .then((groups) => {

        setGroupsData({
          'paginatedGroups': groups,
          'addedGroups': [],
          'removedGroups': [],
        });
        setPaneStatus('onload');

      })
      .catch((error) => {

        console.log('error', error);
        setPaneStatus('error');

      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGroups, getUser, getRoles]);

  //------------------------------------------------------
  //  Edit Groups Modal
  //------------------------------------------------------

  if (!paneOpen) return null;

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  if (paneStatus === 'pending') {

    return (
      <div className='Pane-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container flex flex-col min-w-[950px] items-center'>

          <img className='w-[200px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

          <button className='Primary-Button w-fit self-center' onClick={() => setPaneOpen(false)}>
            Cancel
          </button>

        </dialog>

      </div>
    )

  }

  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  else if (paneStatus === 'onload') {

    return (
      <div className='Pane-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container'>

          <div className='grid grid-rows-[170px_1fr_70px] items-center justify-between w-full overflow-hidden' >

            {/* ------------------------------------------------------ */}
            {/*  Header Container                                      */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-col gap-2'>

              {/* ------------------------------------------------------ */}
              {/*  Header                                                */}
              {/* ------------------------------------------------------ */}

              <div className='flex flex-col gap-1 px-4 py-2 border-solid border-b border-b-[#D8D8D8]'>

                <div className='flex flex-row justify-between'>

                  {/* Title */}
                  <div className='flex flex-col gap-1'>
                    <h5>{title}</h5>
                  </div>

                </div>

              </div>

              {/* ------------------------------------------------------ */}
              {/*  Add Groups Search Bar                                 */}
              {/* ------------------------------------------------------ */}

              <div className='relative my-[10px]  px-4'>

                {/* Search Bar */}
                <div className='flex flex-row items-center'>

                  {/* Search Input Field */}
                  <div className='flex flex-row gap-2 w-full h-[45px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-tl-[5px] '>

                    <img src={SearchIcon} alt='Search-Icon'></img>
                    <input
                      className={
                        groupsData?.searchResults?.length > 0 ?
                          'w-full focus:outline-none'
                          :
                          `w-full focus:outline-none ${(roleStatus === 'read' || roleStatus === 'download' || roleStatus === 'create') && 'cursor-not-allowed bg-[#f8f8f8]'}`
                      }
                      disabled={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
                      type='text'
                      placeholder='Search group by name'
                      value={groupsData?.newGroupInput}
                      onChange={(evt) => {

                        const value = evt.target.value;
                        setGroupsData({ 'newGroupInput': value });

                        // Hide search
                        if (value.length === 0) {

                          return setGroupsData({
                            'newGroupInput': '',
                            'queryMessage': '',
                            'searchResults': []
                          })

                        }

                        // Trigger search
                        handleGroupSearch(value);

                      }}
                      autoComplete='off'
                    ></input>

                  </div>


                  {/* Clear Search Button */}
                  {
                    (groupsData?.newGroupInput?.length || groupsData?.searchResults?.length) > 0 &&

                    <div className='animate-fadein  bg-[#F1F1F1] h-[45px] p-[15px] shadow-md border border-solid border-[#080808] border-l-[none] rounded-tl-none rounded-br-none rounded-tr-[5px] rounded-bl-none text-center align-center cursor-pointer'>

                      <img className='self-center' src={ClearSearch} alt='ClearSearch' onClick={() =>
                        setGroupsData({
                          'newGroupInput': '',
                          'queryMessage': '',
                          'searchResults': []
                        })
                      }></img>

                    </div>
                  }

                </div>

                {/* Search Results */}
                {
                  (groupsData?.searchResults?.length > 0 || groupsData.queryMessage?.length > 0) &&
                  (
                    <div className='absolute z-10 max-h-[350px] w-[calc(100%-48px)] overflow-y-auto bg-white shadow-lg border-[1px] border-[solid] border-[#D2D2D2]'>
                      {
                        groupsData?.searchResults?.map((group, index) => (
                          <div key={index} className='border-b border-b-[#E1E1E1] last:border-0 hover:bg-[#F0F0F0]'>

                            {/* Group Detail */}
                            {
                              // Check if the group exists in the list
                              (defaultGroups.filter((item) => item?.groupid === group?.groupid).length > 0 || groupsData.addedGroups.filter((item) => item?.groupid === group?.groupid).length > 0) ?

                                // Disabled State
                                (
                                  <div className='flex flex-row justify-between py-[10px] px-[15px] cursor-not-allowed'>
                                    <div className='flex flex-col'>
                                      <p className='m-0 font-medium text-[14px] text-[#C4C4C4]'>
                                        {group?.displayname}
                                      </p>
                                      <p className='m-0 text-[14px] text-[#C4C4C4]'>
                                        {group?.groupid}
                                      </p>
                                    </div>

                                    <img className='w-[28px] cursor-not-allowed' src={AddDisabled} alt='Add'></img>
                                  </div>
                                )
                                :

                                // Active State
                                (
                                  <div className='flex flex-row justify-between py-[10px] px-[15px] cursor-pointer' onClick={() => addGroup(group)}>
                                    <div className='flex flex-col'>
                                      <p className='m-0 font-medium text-[14px] text-[#424242] disabled:text-[#C4C4C4]'>
                                        {group?.displayname}
                                      </p>
                                      <p className='m-0 text-[14px] text-[#424242]'>
                                        {group?.groupid}
                                      </p>
                                    </div>

                                    <img className='w-[28px]' src={Add} alt='Add'></img>
                                  </div>
                                )
                            }

                          </div>
                        ))
                      }

                      {/* Total Search Results */}
                      <div className='text-sm py-[10px] px-[15px]'>
                        {groupsData.queryMessage}
                      </div>

                    </div>
                  )
                }

              </div>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Body Container                                        */}
            {/* ------------------------------------------------------ */}

            <div className='w-[88vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

              {/* ------------------------------------------------------ */}
              {/*  Table Container                                       */}
              {/* ------------------------------------------------------ */}

              <div className='Table-Container overflow-hidden'>

                {/* Table header (Grey Section) */}
                <div className='flex flex-row justify-between items-center py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                  {/* Total */}
                  <p className='m-0 py-1 font-medium '>
                    <span className='mr-[5px]'>Total:</span>
                    {
                      // More than 500 groups exist!
                      defaultGroups.length > 500 ? (
                        <>
                          500 of {defaultGroups.length}
                        </>

                      ) : (

                        <>
                          {defaultGroups.length} of {defaultGroups.length}
                        </>
                      )
                    }
                  </p>

                </div>

                {/* Makes the table scrollable */}
                <div className='overflow-auto h-full'>

                  <table className='table-fixed w-full'>

                    <thead>

                      {/* Sticky the header */}
                      <tr className='sticky top-0 drop-shadow-md bg-white border-b-[#D8D8D8] border-solid border-b rounded'>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Name</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Description</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>ID</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'></th>
                      </tr>

                    </thead>

                    <tbody>
                      {
                        groupsData?.paginatedGroups?.length > 0 ?
                          (
                            groupsData?.paginatedGroups?.map((group, index) => (
                              <tr key={index} className='border-b-[#D8D8D8] border-solid border-b rounded last:border-none'>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {group?.displayname} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {group?.description} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {group?.groupid} </td>
                                <td hidden={!(roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')} className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis items-center text-right'>
                                  <div className='Cancel-Icon cursor-pointer w-[28] self-center' onClick={() => removeGroup(group)}></div>
                                </td>
                              </tr>
                            ))

                          )
                          :
                          (
                            <tr className=''>
                              <td colSpan={4} className='py-4 px-3 font-normal break-words text-center align-top'> No groups found.</td>
                            </tr>
                          )
                      }
                    </tbody>

                  </table>

                </div>

              </div>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Buttons Container                                     */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row gap-2 px-4 py-3'>

              <button disabled={(roleStatus === 'read' || roleStatus === 'download' || roleStatus === 'create') || !(groupsData.addedGroups.length > 0 || groupsData.removedGroups.length > 0)} className='Primary-Button' onClick={() => handleSave()}>
                Save
              </button>

              <button className='Secondary-Button' onClick={() => {

                // Clear Search Results
                setGroupsData({
                  'newGroupInput': '',
                  'queryMessage': '',
                  'searchResults': []
                });

                // Close Side Pane
                setPaneOpen(false);

              }}>
                Cancel
              </button>

            </div>

          </div>

        </dialog>

      </div>
    )

  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {

    return (
      <div className='Modal-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container flex flex-col min-w-[950px] justify-center items-center'>

          <img className='my-2 w-[50px]' src={Error} alt='Error'></img>
          <h4>Oops! Something went wrong.</h4>
          <p className='leading-[1.7]'>
            There was an error loading this page.
          </p>
          <button className='Primary-Button w-fit' onClick={() => setPaneOpen(undefined)}>Back</button>

        </dialog>

      </div>

    )

  }

  //------------------------------------------------------
}
